/**
 * storyPresentationMappers — PresentationPlanReadApi + already-loaded
 * StoryWidget[] → SlideVm[].
 *
 * The plan endpoint returns deck structure only (order, cover/closing, and
 * which single canvas item an item slide shows). Titles are always resolved
 * by the backend — the mapper never derives one. Slides are a WINDOW onto the
 * canvas: an item slide renders its referenced canvas widget exactly as the
 * canvas does.
 *
 * A widget still streaming in (referenced but not yet in the canvas cache)
 * lands in pendingWidgetIds so the UI can render a skeleton for it.
 */
import { filterVisibleWidgets } from "../storyCanvas/artifactSectionsMappers";
import type { StoryWidget } from "../dataStories/generateStory.types";
import type {
  PresentationDownloadResponseApi,
  PresentationPlanReadApi,
  PresentationPlanSlideApi,
  PresentationSlideKindApi,
  PresentationVm,
  SlideKind,
  SlideVm,
} from "./storyPresentation.types";

const KIND_MAP: Record<PresentationSlideKindApi, SlideKind> = {
  cover: "cover",
  item: "content",
  closing: "closing",
};

/** Map one plan slide. Cover/closing render the title template; item slides
 *  resolve their single canvas widget against the loaded canvas cache. */
export function mapSlide(
  api: PresentationPlanSlideApi,
  widgetsById: Map<string, StoryWidget>,
): SlideVm {
  const kind = KIND_MAP[api.kind];

  if (api.kind !== "item" || !api.canvas_item_id) {
    return {
      id: api.slide_id,
      index: api.slide_index,
      kind,
      title: api.title,
      layout: "title",
      widgets: [],
      pendingWidgetIds: [],
    };
  }

  const widget = widgetsById.get(api.canvas_item_id);

  return {
    id: api.slide_id,
    index: api.slide_index,
    kind,
    title: api.title,
    layout: "content",
    widgets: widget ? [widget] : [],
    pendingWidgetIds: widget ? [] : [api.canvas_item_id],
  };
}

/** Map the full plan, sorted by slide_index. */
export function mapPresentation(
  api: PresentationPlanReadApi,
  loadedWidgets: StoryWidget[],
): PresentationVm {
  const widgetsById = new Map(loadedWidgets.map((w) => [w.id, w]));

  return {
    slides: [...api.slides]
      .sort((a, b) => a.slide_index - b.slide_index)
      .map((slide) => mapSlide(slide, widgetsById)),
  };
}

/**
 * Sections panel's whole-row toggle, applied to the deck: an item slide
 * shows exactly one canvas widget (see `mapSlide`), so hiding that widget
 * must drop the slide itself — not leave an empty frame in the deck. Cover
 * and closing slides never carry a widget and are never dropped. A slide
 * whose widget is still streaming in (`pendingWidgetIds`) is also kept —
 * only a widget that resolved and was then explicitly hidden empties it out.
 */
export function filterVisibleSlides(
  slides: readonly SlideVm[],
  hiddenIds: ReadonlySet<string>,
): SlideVm[] {
  if (hiddenIds.size === 0) return slides as SlideVm[];

  return slides.reduce<SlideVm[]>((visible, slide) => {
    const widgets = filterVisibleWidgets(slide.widgets, hiddenIds);
    const emptiedByVisibility =
      slide.layout === "content" &&
      widgets.length === 0 &&
      slide.pendingWidgetIds.length === 0;

    if (emptiedByVisibility) return visible;

    visible.push(widgets === slide.widgets ? slide : { ...slide, widgets });
    return visible;
  }, []);
}

/** Below this much remaining lifetime a kept render's signed URLs are
 *  replaced by the fresh response's, so an open deck never ends up holding
 *  links that have expired. */
export const RENDER_URL_MIN_REMAINING_MS = 5 * 60 * 1000;

/**
 * Which render to keep when an open deck re-checks the server. The same
 * `artifact_id` means the content did not change; its URLs are re-signed on
 * every response, so swapping them in would reload every page image for
 * nothing — the previous object is kept while its links are still well
 * within their lifetime. A different artifact (the story changed) always wins.
 */
export function keepRenderIfUnchanged(
  previous: PresentationDownloadResponseApi | undefined,
  next: PresentationDownloadResponseApi,
  nowMs: number,
): PresentationDownloadResponseApi {
  if (!previous || previous.artifact_id !== next.artifact_id) return next;
  const expiresMs = Date.parse(previous.expires_at);
  if (Number.isNaN(expiresMs) || expiresMs - nowMs < RENDER_URL_MIN_REMAINING_MS) return next;
  return previous;
}
